import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import { useMutation, useNotify, useQueryWithStore } from "ra-core";
import React from "react";
import { Booking } from "../../types";
import { OrderActions } from "./util";

interface Props {
  data: Booking;
}

const CancelDialog: React.FC<Props> = (props) => {
  const { data } = props;
  const [open, setOpen] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState("");
  const [cancelCategoryId, setCancelCategoryId] = React.useState<number | "">(
    "",
  );
  const [reasonError, setReasonError] = React.useState(false);
  const notify = useNotify();
  const [mutate, { loading }] = useMutation();

  const { data: categories }: { data?: any[] } = useQueryWithStore({
    type: "getList",
    resource: "cancelation-categories",
    payload: {
      pagination: { perPage: 1000, page: 1 },
      sort: { field: "name", order: "asc" },
      filter: {},
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
    setCancelReason("");
    setReasonError(false);
  };

  const handleClose = () => {
    setOpen(false);
    setCancelReason("");
    setCancelCategoryId("");
    setReasonError(false);
  };

  const handleConfirmCancel = () => {
    if (!cancelReason.trim()) {
      setReasonError(true);
      notify("Cancellation reason is required", "warning");
      return;
    }
    setReasonError(false);

    mutate(
      {
        type: "update",
        resource: "bookings",
        payload: {
          id: data.id ?? "",
          data: {
            action: OrderActions.CANCEL,
            cancellation_reason: cancelReason,
            cancelation_category_id:
              cancelCategoryId !== "" ? cancelCategoryId : undefined,
          },
        },
      },
      {
        onSuccess: ({ data }) => {
          notify(`Booking has been cancelled.`, "info");
          handleClose();
        },
        onFailure: (error) =>
          notify(
            `Error when updating the booking: ${error.message}`,
            "warning",
          ),
      },
    );
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCancelReason(event.target.value);
    if (reasonError && event.target.value.trim()) {
      setReasonError(false);
    }
  };

  const handleCategoryChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setCancelCategoryId(event.target.value as number | "");
  };

  return (
    <>
      <Button color="secondary" onClick={handleClickOpen} size={"small"}>
        Cancel Booking
      </Button>
      <Dialog
        open={open}
        // onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Cancel Booking?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Please provide a reason for cancelling this booking. This
            information is required.
          </DialogContentText>
          <FormControl fullWidth variant="outlined" style={{ marginTop: 16 }}>
            <InputLabel id="cancel-category-label">
              Cancellation Category
            </InputLabel>
            <Select
              labelId="cancel-category-label"
              value={cancelCategoryId}
              onChange={handleCategoryChange}
              label="Cancellation Category"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {categories &&
                categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Cancellation Reason *"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={cancelReason}
            onChange={handleReasonChange}
            error={reasonError}
            helperText={reasonError ? "Cancellation reason is required" : ""}
            placeholder="Please enter the reason for cancelling this booking..."
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Back
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="primary"
            disabled={loading || !cancelReason.trim()}
            variant="contained"
          >
            {loading ? "Please wait..." : "Confirm Cancellation"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CancelDialog;
